import React from 'react';

export interface TopItem {
  label: string;
  count: number;
}

interface MetricCardAction {
  label: string;
  onClick: () => void;
}

interface MetricCardProps {
  title: string;
  value: number;
  accentColor: string;
  /** Up to two most frequent items, shown inline for an at-a-glance read. */
  topItems?: TopItem[];
  /** Small qualifier under the count, e.g. "3 unprocessed". */
  caption?: string;
  primaryAction?: MetricCardAction;
  secondaryAction?: MetricCardAction;
}

/**
 * A compact summary tile: the headline count plus the two most frequent items
 * behind it. Everything else stays behind the actions, which open the full list.
 */
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  accentColor,
  topItems,
  caption,
  primaryAction,
  secondaryAction,
}) => {
  const hasTopItems = Array.isArray(topItems) && topItems.length > 0;

  return (
    <div className="flex h-full flex-col rounded-lg border border-hairline bg-surface p-4 transition-colors hover:border-ink-border">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold uppercase tracking-wide text-ink">{title}</p>
        <span className="text-xl font-semibold tabular-nums" style={{ color: accentColor }}>
          {value?.toLocaleString() ?? 0}
        </span>
      </div>

      {caption && <p className="mt-0.5 text-xs text-ink-muted">{caption}</p>}

      {hasTopItems && (
        <ul className="mt-2.5 space-y-1 border-t border-hairline pt-2.5">
          {topItems?.slice(0, 2).map((item) => (
            <li key={item?.label} className="flex items-baseline gap-2 text-xs">
              <span className="truncate text-ink-secondary" title={item?.label}>
                {item?.label}
              </span>
              <span className="ml-auto shrink-0 font-medium tabular-nums text-ink-muted">
                {item?.count}
              </span>
            </li>
          ))}
        </ul>
      )}

      {(primaryAction || secondaryAction) && (
        <div className="mt-auto flex flex-wrap gap-3 pt-3">
          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              className="text-xs font-medium text-ink-secondary underline-offset-2 hover:text-ink hover:underline"
            >
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              className="text-xs font-medium underline-offset-2 hover:underline"
              style={{ color: accentColor }}
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MetricCard;
